import { useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';

interface Props {
  domains: string[];
  onClose: () => void;
  onCreate: (input: { email_address: string; description?: string }) => Promise<void>;
}

export default function CreateMailboxModal({ domains, onClose, onCreate }: Props) {
  const [local, setLocal] = useState('');
  const [domain, setDomain] = useState(domains[0] || '');
  const [fullAddress, setFullAddress] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasDomains = domains.length > 0;

  async function submit() {
    setError(null);
    const email_address = hasDomains ? `${local.trim()}@${domain}` : fullAddress.trim();
    if (!email_address || (hasDomains && !local.trim())) {
      setError('Enter an address');
      return;
    }
    setBusy(true);
    try {
      await onCreate({ email_address, description: description.trim() || undefined });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to create mailbox');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="New mailbox" size="sm">
      <div className="space-y-3">
        {hasDomains ? (
          <div className="flex gap-2">
            <input
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder="local-part"
              className="h-9 flex-1 min-w-0 rounded-md border border-border-strong bg-surface px-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-border-strong"
            />
            <span className="self-center text-content-subtle">@</span>
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="h-9 rounded-md border border-border-strong bg-surface px-2 text-sm text-content-muted focus:outline-none focus:ring-2 focus:ring-border-strong"
            >
              {domains.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        ) : (
          <input
            value={fullAddress}
            onChange={(e) => setFullAddress(e.target.value)}
            placeholder="mailbox@example.com"
            className="h-9 w-full rounded-md border border-border-strong bg-surface px-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-border-strong"
          />
        )}
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="h-9 w-full rounded-md border border-border-strong bg-surface px-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-border-strong"
        />
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} loading={busy}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}
